import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { ArrowUpRight } from "lucide-react";

const VARIANTS = [
  {
    slug: "inter-display",
    label: "Inter Display",
    subtitle: "Standard premium (Stripe, Notion)",
    body: "Aa Bb Cc Dd Ee",
    sample: "Bonjour Sophie.",
    detail:
      "L'axe optique d'Inter Display resserre naturellement les grands titres et garde une lisibilité chirurgicale.",
    fontClass: "font-inter-body",
    weight: "font-semibold",
    to: "/mockups/dashboard-typo-inter-display",
    color: "#151515",
  },
  {
    slug: "general-sans",
    label: "General Sans",
    subtitle: "Signature géométrique (Polestar, Rivian)",
    body: "Aa Bb Cc Dd Ee",
    sample: "Bonjour Sophie.",
    detail:
      "Plus ronde, plus signature. Elle apporte un ADN 'automobile du futur' immédiatement reconnaissable.",
    fontClass: "font-general-sans",
    weight: "font-semibold",
    to: "/mockups/dashboard-typo-general-sans",
    color: "#151515",
  },
  {
    slug: "neue-haas",
    label: "Neue Haas / Inter Tight",
    subtitle: "Swiss modernism (Linear, Vercel)",
    body: "Aa Bb Cc Dd Ee",
    sample: "Bonjour Sophie.",
    detail:
      "Approche 'Swiss'. Extrêmement neutre, elle laisse toute la place aux photos et à la donnée.",
    fontClass: "font-neue-haas",
    weight: "font-semibold",
    to: "/mockups/dashboard-typo-neue-haas",
    color: "#151515",
  },
];

export default function MockupsIndex() {
  return (
    <>
      <Helmet>
        <title>Mockups Dashboard · wyvio</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <div className="wyvio-signature-bg min-h-screen font-inter-body">
        <main className="relative z-10 mx-auto max-w-[1400px] px-10 py-24">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#E8E8E2] bg-white/70 px-3 py-1.5 text-[10.5px] font-medium uppercase tracking-[0.18em] text-[#6B6B67] backdrop-blur">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#C8FF2F]" />
              Sprint Polish · Direction Artistique
            </div>
            <h1 className="max-w-3xl text-[64px] font-semibold leading-[1.04] tracking-[-0.03em] text-[#151515]">
              Trois typographies. Un seul dashboard.
            </h1>
            <p className="mt-6 max-w-[640px] text-[16px] font-normal leading-relaxed text-[#4A4A46]">
              Le layout, les composants, les photos et la palette sont identiques. Seule la
              famille typographique change. Ouvrez chaque variante, comparez, puis choisissez celle
              qui incarne le mieux WYVIO.
            </p>
          </motion.div>

          <div className="mt-16 grid grid-cols-3 gap-6">
            {VARIANTS.map((v, i) => (
              <motion.div
                key={v.slug}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  delay: 0.1 * i + 0.2,
                  duration: 0.55,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                <Link
                  to={v.to}
                  data-testid={`mockup-card-${v.slug}`}
                  className="shadow-wyvio-elev group relative flex h-full flex-col justify-between overflow-hidden rounded-[24px] bg-white p-8 transition-shadow duration-300 hover:shadow-wyvio-elev-hover"
                >
                  {/* Tag */}
                  <div className="mb-8 flex items-center justify-between">
                    <span className="text-[10.5px] font-medium uppercase tracking-[0.24em] text-[#6B6B67]">
                      Option {String.fromCharCode(65 + i)}
                    </span>
                    <ArrowUpRight
                      className="h-4 w-4 text-[#6B6B67] transition-transform duration-200 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-[#151515]"
                      strokeWidth={2}
                    />
                  </div>

                  {/* Sample H1 */}
                  <div className={`${v.fontClass} mb-10`}>
                    <p
                      className={`font-medium text-[46px] leading-[1.02] tracking-[-0.03em]`}
                      style={{ color: v.color }}
                    >
                      {v.sample}
                    </p>
                    <p className="mt-4 text-[22px] font-normal tracking-[-0.015em] text-[#6B6B67]">
                      {v.body}
                    </p>
                  </div>

                  <div>
                    <p className="text-[19px] font-medium tracking-[-0.015em] text-[#151515]">
                      {v.label}
                    </p>
                    <p className="mt-1 text-[13px] font-normal text-[#6B6B67]">{v.subtitle}</p>
                    <p className="mt-5 text-[13px] font-normal leading-relaxed text-[#4A4A46]">
                      {v.detail}
                    </p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="mt-16 rounded-[20px] border border-[#E8E8E2] bg-white/60 p-8 backdrop-blur">
            <p className="text-[10.5px] font-medium uppercase tracking-[0.24em] text-[#6B6B67]">
              Règle du green
            </p>
            <p className="mt-2 max-w-3xl text-[15px] leading-relaxed text-[#151515]">
              Le vert{" "}
              <span
                className="mx-1 inline-block h-2 w-2 rounded-full align-middle"
                style={{ background: "#C8FF2F" }}
              />
              WYVIO reste rare&nbsp;: un seul CTA principal, un indicateur actif et la
              barre de progression. Le reste est blanc, noir, gris chaud.
            </p>
          </div>
        </main>
      </div>
    </>
  );
}
